import React, { useEffect, useState } from 'react';
import manConversations from '../data/man_conversations.json';
import womanConversations from '../data/woman_conversations.json';
import manSettings from '../data/man_userSettings.json';
import { useMemo } from 'react';
import { usePov } from '../context/PovContext';
import ChatPage from './ChatPage';
import './InboxPage.css';
import { useUI } from '../context/UIContext';

interface Conversation {
  id: string;
  photo?: string;
  catch_phrase: string;
  isBlind?: boolean;
  messages: {
    sender: string;
    message: string;
    timestamp: string;
  }[];
}

const InboxPage: React.FC<{ navId?: string; navToken?: number }> = ({ navId, navToken }) => {
  const { pov } = usePov();
  const { setShowWave } = useUI();
  const conversations = useMemo(() => {
    if (pov === 'woman') {
      return womanConversations;
    }
    // pov === 'man' -> inject opener as first 'them' message
    const opener = (manSettings as any).profile?.opener || '';
    if (!opener) return manConversations;
    return manConversations.map(conv => {
      const messages = [...conv.messages];
      const firstThemIdx = messages.findIndex(m => m.sender === 'them');
      if (firstThemIdx >= 0) {
        messages[firstThemIdx] = { ...messages[firstThemIdx], message: opener };
      } else {
        messages.unshift({ sender: 'them', message: opener, timestamp: new Date().toISOString() });
      }
      return { ...conv, messages };
    });
  }, [pov]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const handleConversationClick = (conversationId: string) => {
    setSelectedConversationId(conversationId);
  };

  const handleBackToInbox = () => {
    setSelectedConversationId(null);
  };

  // Show wave on list, hide wave in chat
  useEffect(() => {
    setShowWave(!selectedConversationId);
  }, [selectedConversationId, setShowWave]);

  // If the Inbox icon is clicked while inside a conversation, return to list
  useEffect(() => {
    if (navId === 'inbox') {
      setSelectedConversationId(null);
    }
  }, [navId, navToken]);

  const getLastMessage = (conversation: Conversation) => {
    const lastMessage = conversation.messages[conversation.messages.length - 1];
    return lastMessage ? lastMessage.message : 'No messages yet';
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (selectedConversationId) {
    const selectedConversation = conversations.find(conv => conv.id === selectedConversationId);
    if (selectedConversation) {
      return (
        <ChatPage 
          conversation={selectedConversation} 
          onBack={handleBackToInbox} 
        />
      );
    }
  }
  

  return (
    <div className="page-content">
      <div className="inbox-container">
        <div className="inbox-header">
          <h1>Messages</h1>
        </div>
        
        <div className="conversations-list">
          {[...conversations].reverse().map((conversation) => {
            const isBlindDate = Boolean(conversation.isBlind);
            return (
              <div 
                key={conversation.id}
                className={`conversation-item ${isBlindDate ? 'blind-date' : ''}`}
                onClick={() => handleConversationClick(conversation.id)}
              >
                <div className="conversation-photo">
                  {conversation.photo ? (
                    <img src={conversation.photo} alt="Profile" />
                  ) : (
                    <div className="blind-placeholder"></div>
                  )}
                </div>
                
                <div className="conversation-details">
                  <div className="conversation-preview">
                    <span className="last-message">{getLastMessage(conversation)}</span>
                  </div>
                  <div className="conversation-time">
                    {conversation.messages.length > 0 && formatTimestamp(conversation.messages[conversation.messages.length - 1].timestamp)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default InboxPage;
